import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Alert, Button, Modal, TextInput } from "@opendate-app/setlist";
import { useSeriesShowContext } from "../SeriesShowApp";

const MAX_CAPACITY = 2147483647;

const InventoryPoolModal = ({ open, pool, onClose }) => {
  const { handleCreateInventoryPool, handleUpdateInventoryPool } = useSeriesShowContext();
  const [name, setName] = useState("");
  const [capacity, setCapacity] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setName(pool ? pool.name : "");
    setCapacity(pool ? String(pool.capacity) : "");
    setSubmitting(false);
    setError(null);
  }, [open, pool?.id]);

  const soldCount = (pool && pool.sold_count) || 0;
  const capacityFloor = Math.max(soldCount, 1);
  const capacityChanged = pool ? capacity !== String(pool.capacity) : true;
  const capacityValid =
    /^\d+$/.test(capacity) &&
    Number(capacity) <= MAX_CAPACITY &&
    (!capacityChanged || Number(capacity) >= capacityFloor);
  const isValid = name.trim() !== "" && capacityValid;

  const submit = async () => {
    if (submitting || !isValid) return;
    try {
      setSubmitting(true);
      setError(null);
      const attributes = { name: name.trim(), ...(capacityChanged ? { capacity: Number(capacity) } : {}) };
      if (pool) {
        await handleUpdateInventoryPool(pool.id, attributes);
      } else {
        await handleCreateInventoryPool(attributes);
      }
      onClose();
    } catch (e) {
      const apiErrors = e && e.response && e.response.data && e.response.data.errors;
      setError(
        Array.isArray(apiErrors)
          ? apiErrors.join(", ")
          : apiErrors || "Could not save this pool. Please try again.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      icon={false}
      title={pool ? "Edit shared inventory" : "Add shared inventory"}
      footer={(
        <>
          <Button onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button
            variant="primary"
            data-testid="inventory-pool-submit"
            onClick={submit}
            disabled={submitting || !isValid}
          >
            {submitting ? "Saving…" : pool ? "Save" : "Add pool"}
          </Button>
        </>
      )}
    >
      <div data-testid="inventory-pool-modal">
        {error && (
          <div className="mb-3" data-testid="inventory-pool-error">
            <Alert color="error" title={error} />
          </div>
        )}
        <div className="mb-3">
          <TextInput
            label="Name"
            id="inventory-pool-name"
            data-testid="inventory-pool-name"
            maxLength={255}
            placeholder="e.g. GA Passes"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <TextInput
          label="Capacity"
          id="inventory-pool-capacity"
          data-testid="inventory-pool-capacity"
          type="number"
          inputMode="numeric"
          min={capacityFloor}
          max={MAX_CAPACITY}
          step="1"
          onWheel={(e) => e.currentTarget.blur()}
          placeholder="How many can be sold across the pool"
          hint={soldCount > 0
            ? `${soldCount} sold so far across this pool, so this can't go below ${soldCount}.`
            : "The total every product in this pool can sell, combined."}
          value={capacity}
          onChange={(e) => setCapacity(e.target.value)}
        />
      </div>
    </Modal>
  );
};

InventoryPoolModal.propTypes = {
  open: PropTypes.bool.isRequired,
  pool: PropTypes.object,
  onClose: PropTypes.func.isRequired,
};

export default InventoryPoolModal;
